import type {
  SectionKey,
  SectionRowMap,
} from './productCardRows'
import type { FigureStats } from './types'
import type {
  CustomViewDataPoint,
  CustomViewSelections,
} from '@/contexts/products/customViewConfig'
import {
  MODEL_VIEW_LOCKED_OFF,
  SM_VIEW_LOCKED_OFF,
} from '@/contexts/products/customViewConfig'
import {
  buildRowMap,
  SECTION_ORDER,
} from './productCardRows'

type ViewMode = 'sm' | 'model'

export type ProductCardVisibility = SectionRowMap & {
  showPicture: boolean
  showDmiSeg: boolean
  showWholeSaleSeg: boolean
  showPrice: boolean
  showCustomerReview: boolean
  showModelVariations: boolean
  showBrand: boolean
  showSizeGrid: boolean
  showGender: boolean
  showProductNature: boolean
  showComposition: boolean
  showEcodesign: boolean
  showBenefits: boolean
  showTeaser: boolean
  showDesignedIn: boolean
  showDistributionCustomsZones: boolean
  showProducedIn: boolean
  showHomologatedIn: boolean
  showAvailability: boolean
  showValidationStatus: boolean
  showMoq: boolean
  showManufacture: boolean
  showSmCompo: boolean
  showModelCompo: boolean
  showFigures: boolean
  visibleFigures: FigureStats[]
  hasContentBelowBasicInfo: boolean
}

const FIGURE_KEY_MAP: Record<FigureStats['name'], CustomViewDataPoint> = {
  gmv: 'gmv',
  sales_qty: 'salesQuantity',
  margin: 'margin',
  cost: 'costPlus',
  markup: 'markup',
  carbon: 'gmvCO2',
}

export function computeProductCardVisibility(viewMode: ViewMode, customView: CustomViewSelections | undefined, figures: FigureStats[]): ProductCardVisibility {
  const isSelected = (key: CustomViewDataPoint): boolean => {
    if (!customView)
      return true
    const lockedOff
      = viewMode === 'sm' ? SM_VIEW_LOCKED_OFF : MODEL_VIEW_LOCKED_OFF
    if (lockedOff.includes(key))
      return false
    return customView.selectedItems.includes(key)
  }

  const showPicture = isSelected('productPicture')
  const showPrice = isSelected('price')
  const showDmiSeg = isSelected('dmiSegmentation')
  const showWholeSaleSeg = isSelected('wholesaleSegmentation')
  const showCustomerReview = isSelected('customerReview')
  const showModelVariations = isSelected('modelVariations')
  const showBrand = isSelected('brand')
  const showSizeGrid = isSelected('sizeGrid')
  const showGender = isSelected('gender')
  const showProductNature = isSelected('productNature')
  const showComposition = isSelected('composition')
  const showEcodesign = isSelected('ecodesignAndCO2')
  const showBenefits = isSelected('productBenefits')
  const showTeaser = isSelected('internetTeaser')
  const showDesignedIn = isSelected('designedIn')
  const showDistributionCustomsZones = isSelected('distributionCustomsZones')
  const showProducedIn = isSelected('producedIn')
  const showHomologatedIn = isSelected('homologatedIn')
  const showAvailability = isSelected('availabilityDates')
  const showValidationStatus = isSelected('validationStatus')
  const showMoq = isSelected('moq')

  const visibleFigures = figures.length > 0
    ? figures.filter(f =>
        isSelected(FIGURE_KEY_MAP[f.name]),
      )
    : []
  const showFigures = visibleFigures.length > 0
  const showManufacture = showDesignedIn || showDistributionCustomsZones || showProducedIn || showHomologatedIn

  const showSmCompo
    = viewMode === 'sm'
      && (showModelVariations
        || showSizeGrid
        || showBrand
        || showGender
        || showProductNature
        || showComposition)

  const showModelCompo
    = viewMode === 'model'
      && (showGender || showBrand || showProductNature || showComposition || showSizeGrid || showValidationStatus)

  const visibleSections = new Set<SectionKey>([
    ...(showSmCompo || showModelCompo ? (['compo'] as const) : []),
    ...(showEcodesign ? (['ecodesign'] as const) : []),
    ...(showBenefits ? (['benefits'] as const) : []),
    ...(showTeaser ? (['teaser'] as const) : []),
    ...(showManufacture ? (['manufacture'] as const) : []),
    ...(showAvailability ? (['availability'] as const) : []),
    ...(showFigures ? (['figures'] as const) : []),
    ...(showMoq ? (['moq'] as const) : []),
  ])

  const { span, row, dividerRow, lastSection } = buildRowMap(SECTION_ORDER, visibleSections)

  return {
    showPicture,
    showDmiSeg,
    showWholeSaleSeg,
    showPrice,
    showCustomerReview,
    showModelVariations,
    showBrand,
    showSizeGrid,
    showGender,
    showProductNature,
    showComposition,
    showEcodesign,
    showBenefits,
    showTeaser,
    showDesignedIn,
    showDistributionCustomsZones,
    showProducedIn,
    showHomologatedIn,
    showAvailability,
    showValidationStatus,
    showMoq,
    showManufacture,
    showSmCompo,
    showModelCompo,
    showFigures,
    visibleFigures,
    hasContentBelowBasicInfo: visibleSections.size > 0,
    span,
    row,
    dividerRow,
    lastSection,
  }
}
